import * as React from 'react'
import { useEffect, useCallback } from 'react'
import FormWrapper from '../form/FormWrapper'
import useFormState from '../form/hooks/useFormState'
import InputField from '../form/InputField'
import FileUpload from '../form/FileUpload'
import isBrandWeBuy from '../form/validators/isBrandWeBuy'
import isNotEmpty from '../form/validators/isNotEmpty'
import cascade from '../form/validators/cascade'
import PropTypes from 'prop-types'
import { BUYING_FORM } from '../../api-common/strapi-v3'
import { StaticImage } from 'gatsby-plugin-image'
import isEmail from './validators/isEmail'
import isPhone from './validators/isPhone'

const CheckboxOptions = ({ option, form, name, onChange }) => {
  const onChangeHandler = e => {
    if (onChange) {
      onChange(e, name)
    }
  }
  return (
    <label className="flex flex-row items-center gap-2">
      <input
        type="checkbox"
        className="peer mt-4 h-4 w-4 border border-gray-400 accent-red-700 sm:h-5 sm:w-5"
        value={option}
        onChange={onChangeHandler}
        checked={form[name]}
      />
      <p className=" inline-block text-gray-400 hover:text-black mt-4 peer-checked:text-black ">
        {option}
        {form[name]}
      </p>
    </label>
  )
}
CheckboxOptions.propTypes = {
  option: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
}

const BuyingForm = ({ type = '', tag = '' }) => {
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (window.fbq) {
        window.fbq.load()
      }
    }, 500)
    return () => {
      clearTimeout(timeout)
    }
  }, [])
  const [form, setForm, updateForm] = useFormState({
    fName: '',
    lName: '',
    phone: '',
    email: '',
    type,
    brand: '',
    model: '',
    caseSize: '',
    box: false,
    papers: false,
    booklets: false,
    extras: false,
    carat: '',
    clarity: '',
    color: '',
    pieces: '',
    weight: '',
    description: '',
    photos: [],
    tag,
    consent: true,
  })
  const onChangeHandler = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.checked }))
  }
  const handleChange = useCallback(
    (e, name) => {
      setForm(f => {
        let newForm = { ...f }
        newForm[name] = e.target.checked
        return newForm
      })
    },
    [setForm]
  )
  const validateWatchBrandExt = useCallback(
    brand => {
      if (form['type'] === 'watches') {
        return cascade(isNotEmpty, isBrandWeBuy)(brand, form)
      }
      return { isValid: true, message: '' }
    },
    [form['type']]
  )
  useEffect(() => {
    setForm(_f => {
      return { ..._f, tag }
    })
  }, [tag])
  const updateUploadedFiles = useCallback(
    files =>
      setForm(f => {
        return { ...f, photos: files }
      }),
    [setForm]
  )
  useEffect(() => {
    window.collectchat = window.collectchat || {}
    if (window.collectchat.unload) {
      window.collectchat.unload()
    }
    window.collectchat.ready = () => {
      for (let i = 1; i < 10; ++i) {
        setTimeout(() => {
          window.collectchat.unload()
        }, 500 * i)
      }
    }
  }, [])
  const rows = () => {
    switch (form.type) {
      case 'watches':
        return 5
      case 'diamonds':
        return 6
      case 'jewelry':
        return 12
      case 'silver':
        return 9
      case 'gold':
        return 6
      default:
        return 12
    }
  }

  const handleTypeChange = (e, newType) => {
    e.preventDefault()
    setForm(f => ({ ...f, type: newType }))
    if (!document) return
    document.getElementById('buying-form').scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const isNullOrEmpty = value => !value
  // eslint-disable-next-line react/prop-types
  const Button = ({ type, formType, children }) => {
    return (
      <button
        className={
          'sm:mx-auto grid grid-cols-[auto_1fr] gap-4 text-lg md:text-xl text-left group items-center justify-start  duration-200 ease-in-out w-full sm:w-1/2 lg:w-full' +
          (type === formType
            ? ' lg:text-red-700 '
            : ' lg:text-gray-700 *:lg:saturate-0 *:hover:saturate-100 ')
        }
        onClick={e => handleTypeChange(e, type)}
      >
        {children}
      </button>
    )
  }

  return (
    <div id="buying-form" className="max-w-[1366px] h-full w-full mx-auto  ">
      <FormWrapper
        className={
          (isNullOrEmpty(form.type)
            ? 'max-h-[37rem] lg:max-h-full '
            : ' max-h-[200vh] -translate-x-[100vw] lg:-translate-x-0') +
          ' py-4 form-checkbox h-full relative grid grid-cols-2 w-[200vw] lg:w-full lg:grid-cols-[auto_1fr] lg:gap-5 duration-500 '
        }
        name={BUYING_FORM}
        action={'/thank-you-buying/'}
        form={form}
        minimumTimeout={1000}
        conversionEventName={'buying_form'}
      >
        <div className={' flex flex-col gap-4 justify-top items-center font-black '}>
          <h2 className="text-xl lg:text-2xl font-black w-full text-center block">
            WHAT WOULD YOU LIKE TO SELL?
          </h2>
          <p className="lg:hidden text-red-700">CLICK TO START YOUR QUOTE</p>
          <div className=" w-screen lg:w-max gap-4 text-sm md:text-lg justify-center items-center flex flex-col px-4 lg:px-0">
            <Button type="watches" formType={form.type || 'watches'}>
              <StaticImage
                src="../../images/luxury-watch-buyers.png"
                alt="silver"
                loading="eager"
                imgStyle={{ objectFit: 'contain' }}
                sizes="5rem"
                className="aspect-square  w-full max-w-20 "
              />
              LUXURY WATCHES
            </Button>
            <Button type="jewelry" formType={form.type}>
              <StaticImage
                src="../../images/jewelry-buyers.png"
                alt="silver"
                loading="eager"
                imgStyle={{ objectFit: 'contain' }}
                sizes="5rem"
                className="aspect-square w-full max-w-20 "
              />
              FINE JEWELRY
            </Button>
            <Button type="diamonds" formType={form.type}>
              <StaticImage
                src="../../images/loose-diamond-buyers.png"
                alt="silver"
                loading="eager"
                imgStyle={{ objectFit: 'contain' }}
                sizes="5rem"
                className="aspect-square w-full max-w-20 "
              />
              LOOSE DIAMONDS
            </Button>
            <Button type="silver" formType={form.type}>
              <StaticImage
                src="../../images/sterling-silver-buyers.png"
                alt="silver"
                loading="eager"
                imgStyle={{ objectFit: 'contain' }}
                sizes="5rem"
                className="aspect-square w-full max-w-20 "
              />
              SILVER FLATWARE
            </Button>
            <Button type="gold" formType={form.type}>
              <StaticImage
                src="../../images/coins-and-bullions.png"
                alt="gold"
                loading="eager"
                imgStyle={{ objectFit: 'contain' }}
                sizes="5rem"
                className="aspect-square w-full max-w-20 "
              />
              COINS AND BULLIONS
            </Button>
          </div>
        </div>
        <div className={'flex flex-col space-y-4 h-full relative px-5 lg:px-0 '}>
          <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
            <InputField
              name={'fName'}
              label={'First Name'}
              form={form}
              onChange={updateForm}
              required={true}
            />
            <InputField
              name={'lName'}
              label={'Last Name'}
              form={form}
              onChange={updateForm}
              required={true}
            />
            <InputField
              name={'email'}
              label={'E-Mail'}
              form={form}
              validator={isEmail}
              onChange={updateForm}
              required={true}
            />
            <InputField
              name={'phone'}
              label={'Phone'}
              form={form}
              validator={isPhone}
              onChange={updateForm}
              required={true}
            />
            <div className="md:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <label className="text-xs text-gray-500 flex flex-row gap-3 items-start my-2">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form['consent']}
                  onChange={onChangeHandler}
                />
                I agree to receive updates and follow-up messages about my inquiries via text and
                email.
              </label>
              <p className="h-full flex items-center font-bold">
                Is there a specific amount you would like to receive?
              </p>
            </div>
            <InputField
              name={'amount'}
              label={'Desired Amount'}
              form={form}
              onChange={updateForm}
              required={false}
            />
          </div>
          <div className={'col-span-1 sm:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-4 '}>
            <div className="w-full">
              {/* <div className=" flex flex-row relative group pr-4 ">
                  <div className="text-sm text-gray-400 absolute z-40 -rotate-45 right-4 group-hover:text-accent top-0 translate-y-2">
                    &#9699;
                  </div>
                  <select
                    name="type"
                    id="type"
                    onChange={updateForm}
                    className="relative max-w-max"
                  >
                    <option value="watches">WATCH</option>
                    <option value="jewelry">JEWELRY</option>
                    <option value="diamonds">DIAMOND</option>
                    <option value="silver">STERLING SILVER</option>
                  </select>
                </div> */}

              {form.type === 'watches' && (
                <>
                  <div className="flex flex-row relative h-12 max-w-max border border-gray-200 mt-4">
                    <div className="text-sm text-red-700 absolute -rotate-45 group-hover:text-red-600 top-2 right-3 z-10">
                      &#9699;
                    </div>
                    <select
                      name="brand"
                      required
                      id="brand"
                      onChange={updateForm}
                      className="px-3 h-full cursor-pointer appearance-none pr-10"
                    >
                      <option value="">Select Brand *</option>
                      <option value="A. Lange & Söhne">A. Lange & Söhne</option>
                      <option value="Audemars Piguet">Audemars Piguet</option>
                      <option value="Bedat">Bedat</option>
                      <option value="Bell & Ross">Bell & Ross</option>
                      <option value="Bedat">Bedat</option>
                      <option value="Blancpain">Blancpain</option>
                      <option value="Bedat">Bedat</option>
                      <option value="Breguet">Breguet</option>
                      <option value="Breitling">Breitling</option>
                      <option value="Cartier">Cartier</option>
                      <option value="Chanel">Chanel</option>
                      <option value="Chopard">Chopard</option>
                      <option value="Chronoswiss">Chronoswiss</option>
                      <option value="Concord">Concord</option>
                      <option value="Corum">Corum</option>
                      <option value="David Yurman">David Yurman</option>
                      <option value="Ebel">Ebel</option>
                      <option value="Ernst Benz">Ernst Benz</option>
                      <option value="F. P. Journe">F. P. Journe</option>
                      <option value="Franck Muller">Franck Muller</option>
                      <option value="Gerald Genta">Gerald Genta</option>
                      <option value="Girard-Perregaux">Girard-Perregaux</option>
                      <option value="Glashutte">Glashutte</option>
                      <option value="Harry Winston">Harry Winston</option>
                      <option value="Hublot">Hublot</option>
                      <option value="IWC">IWC</option>
                      <option value="Jacob & Co.">Jacob & Co.</option>
                      <option value="Jaeger-LeCoultre">Jaeger-LeCoultre</option>
                      <option value="Louis Vuitton">Louis Vuitton</option>
                      <option value="Maurice Lacroix">Maurice Lacroix</option>
                      <option value="Montblanc">Montblanc</option>
                      <option value="Omegap-4">Omega</option>
                      <option value="Panerai">Panerai</option>
                      <option value="Parmigiani Fleurier">Parmigiani Fleurier</option>
                      <option value="Patek Philippe">Patek Philippe</option>
                      <option value="Piaget">Piaget</option>
                      <option value="Richard Mille">Richard Mille</option>
                      <option value="Roger Dubuis">Roger Dubuis</option>
                      <option value="Rolex">Rolex</option>
                      <option value="Tag Heuer">Tag Heuer</option>
                      <option value="Tiffany & Co.">Tiffany & Co.</option>
                      <option value="Tudor">Tudor</option>
                      <option value="Ulysse Nardin">Ulysse Nardin</option>
                      <option value="Vacheron Constantin">Vacheron Constantin</option>
                      <option value="Van Cleef & Arpels">Van Cleef & Arpels</option>
                      <option value="Zenith">Zenith</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      name={'model'}
                      label={'Watch Model'}
                      form={form}
                      onChange={updateForm}
                      validator={validateWatchBrandExt}
                      required={false}
                      className="col-span-2 lg:col-span-1"
                    />

                    <InputField
                      name={'caseSize'}
                      label={'Case Size (mm)'}
                      form={form}
                      onChange={updateForm}
                      validator={validateWatchBrandExt}
                      required={false}
                      className="col-span-2 lg:col-span-1"
                    />
                  </div>
                  <div className={' flex flex-row justify-between text-sm md:text-base '}>
                    <CheckboxOptions
                      form={form}
                      onChange={handleChange}
                      name={'box'}
                      option={'Box'}
                    />
                    <CheckboxOptions
                      form={form}
                      onChange={handleChange}
                      name={'papers'}
                      option={'Papers'}
                    />
                    <CheckboxOptions
                      form={form}
                      onChange={handleChange}
                      name={'booklets'}
                      option={'Booklets'}
                    />
                    <CheckboxOptions
                      form={form}
                      onChange={handleChange}
                      name={'extras'}
                      option={'Extras'}
                    />
                  </div>
                </>
              )}

              {form.type === 'diamonds' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      name={'carat'}
                      label={'Carat Weight'}
                      form={form}
                      onChange={updateForm}
                    />
                    <InputField
                      name={'clarity'}
                      label={'Clarity'}
                      form={form}
                      onChange={updateForm}
                    />
                  </div>
                  <InputField name={'color'} label={'Color'} form={form} onChange={updateForm} />
                </>
              )}

              {form.type === 'silver' && (
                <div className="grid grid-cols-2 gap-4">
                  <InputField
                    name={'pieces'}
                    label={'Total Pieces'}
                    form={form}
                    onChange={updateForm}
                    required={true}
                  />
                  <InputField
                    name={'weight'}
                    label={'Weight in grams'}
                    form={form}
                    onChange={updateForm}
                    required={true}
                  />
                </div>
              )}
              {form.type === 'gold' && (
                <div className="grid grid-cols-2 gap-4">
                  <InputField
                    name={'pieces'}
                    label={'Total Pieces'}
                    form={form}
                    onChange={updateForm}
                    required={true}
                  />
                  <InputField
                    name={'weight'}
                    label={'Total Weight'}
                    form={form}
                    onChange={updateForm}
                    required={false}
                  />
                  <div className="flex flex-row relative h-12 w-max border border-gray-200 mt-4">
                    <div className="text-sm text-red-700 absolute -rotate-45 group-hover:text-red-600 top-2 right-3 z-10">
                      &#9699;
                    </div>
                    <select
                      name="brand"
                      id="brand"
                      onChange={updateForm}
                      className="px-5 h-full cursor-pointer appearance-none pr-10"
                    >
                      <option value="">Metal Type & Purity</option>
                      <option value="14k Gold">14k Gold</option>
                      <option value="18k Gold">18k Gold</option>
                      <option value="24k Gold">24k Gold</option>
                      <option value="925 Sterling Silver">925 Sterling Silver</option>
                      <option value="800 Silver">800 Silver</option>
                      <option value="Platinum">Platinum</option>
                    </select>
                  </div>
                </div>
              )}

              <InputField
                type={'textarea'}
                rows={rows()}
                name={'description'}
                label={'Description'}
                form={form}
                onChange={updateForm}
              />
            </div>
            <div className="w-full mt-2 lg:translate-y-2 ">
              <FileUpload
                id={'photos'}
                name={'photos'}
                label={'Photos'}
                accept={'.jpg,.png,.jpeg'}
                UploadClassName={'w-full'}
                required={true}
                updateFilesCb={updateUploadedFiles}
                multiple
              />
            </div>
            <div className="flex flex-row gap-4">
              <button
                onClick={e => handleTypeChange(e, '')}
                className="bg-gray-700 hover:bg-gray-600 text-white max-w-max rounded-lg px-5 py-3 mx-auto md:mx-0 lg:hidden"
              >
                BACK
              </button>
              <button className="bg-red-700 hover:bg-red-600 text-white max-w-max rounded-lg px-5 py-3 mx-auto md:mx-0">
                SUBMIT FORM
              </button>
            </div>
          </div>
        </div>
      </FormWrapper>
    </div>
  )
}
BuyingForm.propTypes = {
  type: PropTypes.string,
  tag: PropTypes.string,
}

export default BuyingForm
